import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const RTOUser = () => {
  const { rtoNumber } = useParams();
  const navigate = useNavigate();
  const [soldNumbersInput, setSoldNumbersInput] = useState('');
  const [soldNumbers, setSoldNumbers] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [duplicateCounts, setDuplicateCounts] = useState({});
  const [filter, setFilter] = useState('all');
  const [exportFormat, setExportFormat] = useState('excel');

  const allNumbers = Array.from({ length: 10000 }, (_, i) => 
    i.toString().padStart(4, '0')
  );

  useEffect(() => {
    const loadSoldNumbers = async () => {
      try {
        setLoading(true);
        setErrorMessage('');
        
        try {
          const response = await fetch(`https://api.dremerz.net/api/DZ_8923_RTOProject_RTOTable_RTOSoldVehicleNumbers?RTONum=${rtoNumber}`);
          
          if (response.ok) {
            const data = await response.json();
            if (data.length > 0) {
              // Combine all sold numbers from all records for this RTO
              const allSoldNumbers = data.reduce((acc, record) => {
                const numbers = record.SoldVehicleNum.split(/\s+/).filter(num => num.trim() !== '');
                return [...acc, ...numbers];
              }, []);
              
              setSoldNumbers(allSoldNumbers);
              
              // Calculate duplicate counts
              const counts = {};
              allSoldNumbers.forEach(num => {
                counts[num] = (counts[num] || 0) + 1;
              });
              setDuplicateCounts(counts);
            }
          } else {
            throw new Error('API request failed');
          }
        } catch (apiError) {
          console.warn('API unavailable, using local storage only:', apiError);
          setApiAvailable(false);
          
          const localData = localStorage.getItem(`soldNumbers_${rtoNumber}`);
          if (localData) {
            const numbers = JSON.parse(localData);
            setSoldNumbers(numbers);
            
            const counts = {};
            numbers.forEach(num => {
              counts[num] = (counts[num] || 0) + 1;
            });
            setDuplicateCounts(counts);
          }
        }
      } catch (error) {
        console.error('Error loading sold numbers:', error);
        setErrorMessage('Failed to load sold numbers');
      } finally {
        setLoading(false);
      }
    };
    
    loadSoldNumbers();
  }, [rtoNumber]);

  const handleSoldNumbersChange = (e) => {
    const value = e.target.value.replace(/[^\d, ]/g, '');
    setSoldNumbersInput(value);
  };

  const saveSoldNumbers = async (newNumbersArray) => {
    try {
      setLoading(true);
      setErrorMessage('');
      
      // Create a new record for this batch of numbers
      const data = {
        RTONum: rtoNumber,
        SoldVehicleNum: newNumbersArray.join(' '),
        createdAt: new Date().toISOString()
      };

      if (apiAvailable) {
        // Always create a new record for this batch
        const createResponse = await fetch('https://api.dremerz.net/api/DZ_8923_RTOProject_RTOTable_RTOSoldVehicleNumbers', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });
        
        if (!createResponse.ok) {
          throw new Error('Failed to create record');
        }
      }
      
      // Update local storage with all numbers (existing + new)
      const allNumbers = [...soldNumbers, ...newNumbersArray];
      localStorage.setItem(`soldNumbers_${rtoNumber}`, JSON.stringify(allNumbers));
      
      return allNumbers;
    } catch (error) {
      console.error('Error saving sold numbers:', error);
      setErrorMessage(`Failed to save numbers${apiAvailable ? '' : ' (offline mode)'}`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const processSoldNumbers = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      
      const numbersArray = soldNumbersInput
        .split(/[, ]+/)
        .filter(num => num.trim() !== '')
        .map(num => num.padStart(4, '0'));

      const invalidNumbers = numbersArray.filter(num => !/^\d{4}$/.test(num));
      if (invalidNumbers.length > 0) {
        setErrorMessage(`Invalid numbers detected: ${invalidNumbers.join(', ')}`);
        return;
      }

      const newDuplicates = {};
      numbersArray.forEach(num => {
        if (soldNumbers.includes(num)) {
          newDuplicates[num] = (newDuplicates[num] || 0) + 1;
        }
      });
      
      const updatedNumbers = await saveSoldNumbers(numbersArray);
      
      if (updatedNumbers) {
        setSoldNumbers(updatedNumbers);
        
        const counts = {};
        updatedNumbers.forEach(num => {
          counts[num] = (counts[num] || 0) + 1;
        });
        setDuplicateCounts(counts);
        
        let successMsg = `${numbersArray.length} numbers processed successfully${!apiAvailable ? ' (offline mode)' : ''}!`;
        
        const duplicateEntries = Object.entries(newDuplicates);
        if (duplicateEntries.length > 0) {
          successMsg += ` (${duplicateEntries.length} duplicates found)`;
        }
        
        setSuccessMessage(successMsg);
        setSoldNumbersInput('');
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      }
    } catch (error) {
      setErrorMessage('Failed to process numbers. Please try again.');
      console.error('Error processing numbers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    try {
      let exportData = [];
      
      if (filter === 'all') {
        exportData = allNumbers.map(number => {
          const count = duplicateCounts[number] || 0;
          return {
            Number: number,
            Status: count > 0 ? (count > 1 ? 'Sold' : 'Sold') : 'Available',
            Count: count > 0 ? count : 0
          };
        });
      } else {
        exportData = filteredNumbers.map(number => {
          const count = duplicateCounts[number] || 0;
          return {
            Number: number,
            Status: filter === 'available' ? 'Available' : 'Sold',
            Count: count
          };
        });
      }

      if (exportFormat === 'excel') {
        exportToExcel(exportData);
      } else if (exportFormat === 'pdf') {
        exportToPDF(exportData);
      }
      
      setSuccessMessage(`Exported ${exportData.length} numbers as ${exportFormat.toUpperCase()} successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage(`Failed to export ${exportFormat.toUpperCase()} data. Please try again.`);
      console.error('Export error:', error);
    }
  };

  const exportToExcel = (data) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const colWidths = [{ wch: 10 }, { wch: 15 }, { wch: 10 }];
    worksheet['!cols'] = colWidths;
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `${rtoNumber}_${filter}`);
    XLSX.writeFile(workbook, `${rtoNumber}_${filter}_numbers.xlsx`);
  };

  const exportToPDF = (data) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`RTO ${rtoNumber} - ${filter.charAt(0).toUpperCase() + filter.slice(1)} Numbers`, 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);
    
    const tableData = data.map(item => [
      item.Number, 
      item.Status,
      item.Count > 0 ? item.Count.toString() : '-'
    ]);
    
    autoTable(doc, {
      head: [['Number', 'Status', 'Count']],
      body: tableData,
      startY: 30,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [66, 139, 202] },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { top: 30 },
      didDrawPage: function (data) {
        doc.setFontSize(8);
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, data.settings.margin.left, pageHeight - 10);
      }
    });
    
    doc.save(`${rtoNumber}_${filter}_numbers.pdf`);
  };

  const getNumberStatus = (number) => {
    const count = duplicateCounts[number] || 0;
    return {
      isSold: count > 0,
      count: count
    };
  };

  const filteredNumbers = allNumbers.filter(number => {
    const { isSold, count } = getNumberStatus(number);
    
    switch (filter) {
      case 'sold': return isSold;
      case 'repeated': return count > 1;
      case 'available': return !isSold;
      default: return true;
    }
  });

  const uniqueSoldCount = Object.keys(duplicateCounts).length;
  const repeatedCount = Object.values(duplicateCounts).filter(count => count > 1).length;
  const availableCount = 10000 - uniqueSoldCount;

  const handleClose = () => {
    navigate('/');
  };

  return (
    <div className="card mt-4 shadow-lg m-3">
      <div className="card-header text-white d-flex justify-content-between align-items-center" style={{        background: 'linear-gradient(145deg, #1a2980, #26d0ce)',
}}>
        <h3 className="mb-0">RTO: {rtoNumber}</h3>
        <button 
          onClick={handleClose} 
          className="btn btn-sm btn-light"
          disabled={loading}
        >
          <i className="bi bi-x-lg"></i> Close
        </button>
      </div>
      
      <div className="card-body">
        <div className="mb-4">
          <label htmlFor="soldNumbers" className="form-label">
            Enter sold numbers (separate by commas or spaces):
          </label>
          <div className="input-group">
            <input
              type="text"
              id="soldNumbers"
              className="form-control"
              placeholder="e.g., 1234 5678, 9012 or 0001,0002,0003"
              value={soldNumbersInput}
              onChange={handleSoldNumbersChange}
              disabled={loading}
            />
            <button
              onClick={processSoldNumbers}
              className="btn btn-primary"
              disabled={loading || !soldNumbersInput.trim()}
            >
              {loading ? (
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              ) : (
                <i className="bi bi-check-circle me-1"></i>
              )}
              {loading ? 'Processing...' : 'Save'}
            </button>
          </div>
          <div className="form-text">
            Enter 4-digit numbers separated by commas or spaces
          </div>
        </div>

        {errorMessage && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {errorMessage}
          </div>
        )}
        {successMessage && (
          <div className="alert alert-success">
            <i className="bi bi-check-circle-fill me-2"></i>
            {successMessage}
          </div>
        )}

        <div className="btn-group mb-3 w-100" role="group">
          <button 
            type="button" 
            className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('all')}
          >
            All Numbers
          </button>
          <button 
            type="button" 
            className={`btn ${filter === 'sold' ? 'btn-danger' : 'btn-outline-danger'}`}
            onClick={() => setFilter('sold')}
          >
            Sold ({uniqueSoldCount})
          </button>
          <button 
            type="button" 
            className={`btn ${filter === 'repeated' ? 'btn-warning' : 'btn-outline-warning'}`}
            onClick={() => setFilter('repeated')}
          >
            Repeated ({repeatedCount})
          </button>
          <button 
            type="button" 
            className={`btn ${filter === 'available' ? 'btn-success' : 'btn-outline-success'}`}
            onClick={() => setFilter('available')}
          >
            Available ({availableCount})
          </button>
        </div>

        <div className="mt-2">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              Showing: {filter === 'all' ? 'All' : filter === 'sold' ? 'Sold' : filter === 'repeated' ? 'Repeated' : 'Available'} Numbers
              ({filteredNumbers.length} of 10,000)
            </h5>
            <div className="d-flex">
              <div className="btn-group me-2">
                <button 
                  type="button" 
                  className={`btn btn-sm ${exportFormat === 'excel' ? 'btn-success' : 'btn-outline-success'}`}
                  onClick={() => setExportFormat('excel')}
                >
                  <i className="bi bi-file-earmark-excel me-1"></i> Excel
                </button>
                <button 
                  type="button" 
                  className={`btn btn-sm ${exportFormat === 'pdf' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setExportFormat('pdf')}
                >
                  <i className="bi bi-file-earmark-pdf me-1"></i> PDF
                </button>
              </div>
              <button 
                onClick={handleExport}
                className={`btn ${exportFormat === 'excel' ? 'btn-success' : 'btn-danger'}`}
                disabled={loading}
              >
                <i className="bi bi-download me-1"></i> Export
              </button>
            </div>
          </div>
          
          {!apiAvailable && (
            <div className="alert alert-warning mb-3">
              <i className="bi bi-wifi-off me-2"></i>
              <strong>Offline Mode:</strong> Changes will be saved locally until API is available.
            </div>
          )}
          
          <div className="numbers-grid">
            {filteredNumbers.map((number) => {
              const { isSold, count } = getNumberStatus(number);
              return (
                <div 
                  key={number} 
                  className={`number-cell ${isSold ? 'sold' : 'available'}`}
                  title={
                    isSold 
                      ? count > 1 
                        ? `Sold (${count} times): ${number}`
                        : `Sold: ${number}`
                      : `Available: ${number}`
                  }
                >
                  {number}
                  {count > 1 && <span className="duplicate-badge">{count}</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style jsx>{`
        .numbers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
          gap: 8px;
          padding: 8px;
          border-radius: 4px;
          width: 100%;
        }
        
        .number-cell {
          position: relative;
          padding: 6px;
          text-align: center;
          border-radius: 4px;
          font-size: 0.85rem;
          cursor: default;
          transition: all 0.2s;
        }
        
        .number-cell.available {
          background-color: #e8f5e9;
          color: #2e7d32;
          border: 1px solid #c8e6c9;
        }
        
        .number-cell.sold {
          background-color: #ffebee;
          color: #c62828;
          border: 1px solid #ef9a9a;
          font-weight: bold;
        }
        
        .number-cell:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .duplicate-badge {
          position: absolute;
          top: -5px;
          right: -5px;
          background-color: #ff5722;
          color: white;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          font-size: 0.6rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </div>
  );
};

export default RTOUser;